import React, { useState, useEffect } from "react";
import MDBadgeDot from "components/MDBadgeDot";
import MDBox from "components/MDBox";
import Grid from "@mui/material/Grid";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import dashboardService from "services/dashboardService";
import { CallMetrics } from "home/models";
import "chartjs-adapter-moment";
import { toast } from "react-toastify";
import { Skeleton } from "@mui/material";

const DashboardCharts = () => {
  const [autoDialerMetrics, setAutoDialerMetrics] = useState<CallMetrics>(undefined);
  const [bnsfExceptions, setBnsfExceptions] = useState<any>(undefined);
  const [bnsfRequests, setBnsfRequests] = useState<any>(undefined);
  const [upMessageMetrics, setUpMessageMetrics] = useState<any>(undefined);
  const [csxMessageMetrics, setCsxMessageMetrics] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    var metricsPromise = dashboardService.getDialerMetrics();

    metricsPromise.then((result) => setAutoDialerMetrics(result));
    var exceptionsPromise = dashboardService;
    exceptionsPromise
      .getExceptions()
      .then((r) => {
        var chartData = r?.tables[0]?.rows.map((m) => ({ x: m[0], y: m[1] }));
        setBnsfExceptions(chartData);
      })
      .catch(() => {
        toast.error("Error getting BNSF Exceptions");
      });

    var requestsPromise = dashboardService.getRequests();
    requestsPromise
      .then((r) => {
        var chartData = r?.tables[0]?.rows.map((m) => ({ x: m[0], y: m[1] }));
        setBnsfRequests(chartData);
      })
      .catch(() => {
        toast.error("Error getting BNSF Requests");
      });

    var upPromise = dashboardService.getUpMessageMetrics();
    upPromise
      .then((r) => {
        var chartData = r?.tables[0]?.rows.map((m) => ({ x: m[0], y: m[1] }));
        setUpMessageMetrics(chartData);
      })
      .catch(() => {
        toast.error("Error getting Up Message Metrics");
      });

    var csxPromise = dashboardService.getCsxMessageMetrics();
    csxPromise
      .then((r) => {
        var chartData = r?.tables[0]?.rows.map((m) => ({ x: m[0], y: m[1] }));
        setCsxMessageMetrics(chartData);
      })
      .catch(() => {
        toast.error("Error getting Csx Message Metrics");
      });

    Promise.all([metricsPromise, exceptionsPromise, requestsPromise, upPromise, csxPromise]).finally(() => {
      setIsLoading(false);
    });
  }, []);

  const getBadgeColor = (metricType: string, value: number) => {
    switch (metricType) {
      case "15":
        if (value <= 1) return "error";
        else if (value > 1 && value <= 5) return "warning";
        else return "success";
      case "30":
        if (value <= 4) return "error";
        else if (value > 4 && value <= 9) return "warning";
        else return "success";
      case "60":
        if (value <= 9) return "error";
        else if (value > 9 && value <= 29) return "warning";
        else return "success";
      default:
        return "info";
    }
  };

  const skeleton = () => {
    return (
      <>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}> <Skeleton variant="rectangular" height={300} /></Grid>
          <Grid item xs={12} md={6}> <Skeleton variant="rectangular" height={300} /></Grid>
          <Grid item xs={12} md={6}> <Skeleton variant="rectangular" height={300} /></Grid>
          <Grid item xs={12} md={6}> <Skeleton variant="rectangular" height={300} /></Grid>
        </Grid>
      </>
    );
  };

  const charts = () => {
    return (
      <>
        <MDBox  p={4} mb={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <ComplexStatisticsCard
                color={getBadgeColor("15", autoDialerMetrics?.callCount15)}
                badgeText="15"
                title="Auto Dialer Call Activity"
                count={autoDialerMetrics?.callCount15}
                percentage={{
                  label: "Call count for 15 minute intervals",
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={3}>
                <ComplexStatisticsCard
                  color={getBadgeColor("30", autoDialerMetrics?.callCount30)}
                  badgeText="30"
                  title="Auto Dialer Call Activity"
                  count={autoDialerMetrics?.callCount30}
                  percentage={{
                    label: "Call count for 30 minute intervals",
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <ComplexStatisticsCard
                color={getBadgeColor("60", autoDialerMetrics?.callCount60)}
                badgeText="60"
                title="Auto Dialer Call Activity"
                count={autoDialerMetrics?.callCount60}
                percentage={{
                  label: "Call count for 60 minute intervals",
                }}
              />
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MDBox mt={2}>
              <VerticalBarChart
                title="BNSF Integration Requests - Last 6 hours"
                description={
                  <MDBox display="flex" justifyContent="space-between">
                    <MDBox display="flex" ml={-1}>
                      <MDBadgeDot color="info" size="sm" badgeContent="RCX BNSF Integration Requests" />
                    </MDBox>
                  </MDBox>
                }
                chart={{
                  labels: [],
                  datasets: [
                    {
                      label: "BNSF Requests",
                      color: "info",
                      data: bnsfRequests,
                    },
                  ],
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <MDBox mt={2}>
              <VerticalBarChart
                title="BNSF Integration Exceptions - Last 6 hours"
                description={
                  <MDBox display="flex" justifyContent="space-between">
                    <MDBox display="flex" ml={-1}>
                      <MDBadgeDot color="info" size="sm" badgeContent="RCX BNSF Integration Exceptions" />
                    </MDBox>
                  </MDBox>
                }
                chart={{
                  labels: [],
                  datasets: [
                    {
                      label: "BNSF Exceptions",
                      color: "error",
                      data: bnsfExceptions,
                    },
                  ],
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <MDBox mt={2}>
              <VerticalBarChart
                title="Up Message Count Metrics - Last 6 hours"
                description={
                  <MDBox display="flex" justifyContent="space-between">
                    <MDBox display="flex" ml={-1}>
                      <MDBadgeDot color="info" size="sm" badgeContent="Up Message Count Metrics" />
                    </MDBox>
                  </MDBox>
                }
                chart={{
                  labels: [],
                  datasets: [
                    {
                      label: "Up Message Count",
                      color: "info",
                      data: upMessageMetrics,
                    },
                  ],
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <MDBox mt={2}>
              <VerticalBarChart
                title="CSX Message Count Metrics - Last 6 hours"
                description={
                  <MDBox display="flex" justifyContent="space-between">
                    <MDBox display="flex" ml={-1}>
                      <MDBadgeDot color="info" size="sm" badgeContent="CSX Message Count Metrics" />
                    </MDBox>
                  </MDBox>
                }
                chart={{
                  labels: [],
                  datasets: [
                    {
                      label: "CSX Message Count",
                      color: "info",
                      data: csxMessageMetrics,
                    },
                  ],
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </>
    );
  };
  return isLoading ? skeleton() : charts();
};
export default DashboardCharts;
